import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Badge } from '../ui/Badge'
import { MANUAL_PAYMENT_METHODS, type DonationRecord, type ManualPaymentMethod } from '../../services/allodons'

/**
 * DONS-MANUELS-B (#403) — ce qu'une ligne de don DIT de son origine et de son
 * rapprochement, partout où `DonationRecord` est rendu (bloc Dons de la fiche,
 * liste dépliée, onglet Dons de la scène via `RecentDonationsList`) :
 *
 *   - `source = MANUAL` ⇒ « Saisi à la main » (neutre : une origine est une
 *     donnée, pas un jugement — design system §5septies) ;
 *   - sur la ligne MANUAL seulement (recette R1 : c'est elle qui est
 *     suspendue — le don AlloDons compte toujours) : `TO_RECONCILE` ⇒ « À
 *     rapprocher » (`warning` : un humain doit agir) ; `RECONCILED` ⇒
 *     « Doublon confirmé » (neutre), la ligne est atténuée par l'appelant ;
 *   - sur la ligne AlloDons jumelée EN ATTENTE : « Jumeau en attente »
 *     (neutre, elle est comptée) ; après `RECONCILED` / `DISMISSED`, rien ;
 *   - `DISMISSED` et `NONE` ⇒ rien : deux dons distincts sont deux dons.
 *
 * Un serveur d'avant le lot ne rend ni `source` ni `reconciliationStatus` :
 * aucun badge, la ligne est celle d'avant.
 */
export function DonationSourceBadge({ donation }: { donation: Pick<DonationRecord, 'source'> }) {
  const { t } = useTranslation('donations')
  if (donation.source !== 'MANUAL') return null
  return <Badge>{t('donations:manual.badges.source')}</Badge>
}

export function DonationReconciliationBadge({
  donation,
}: {
  donation: Pick<DonationRecord, 'source' | 'reconciliationStatus'>
}) {
  const { t } = useTranslation('donations')
  const status = donation.reconciliationStatus
  if (donation.source !== 'MANUAL') {
    return status === 'TO_RECONCILE' ? <Badge>{t('donations:manual.badges.twinPending')}</Badge> : null
  }
  if (status === 'TO_RECONCILE') return <Badge tone="warning">{t('donations:manual.badges.toReconcile')}</Badge>
  if (status === 'RECONCILED') return <Badge>{t('donations:manual.badges.reconciled')}</Badge>
  return null
}

/**
 * Qui a saisi un don MANUAL : « Saisi par vous » quand l'auteur est la
 * session (priorité), « Saisi par <nom> » quand le serveur rend
 * `agentUserName`, rien sinon (serveur d'avant, ou auteur inconnu). Un don
 * AlloDons n'a pas d'auteur.
 */
export function recordedByLabel(
  t: TFunction,
  donation: Pick<DonationRecord, 'source' | 'agentUserId' | 'agentUserName'>,
  currentUserId: string | null | undefined,
): string | null {
  if (donation.source !== 'MANUAL') return null
  if (currentUserId && donation.agentUserId === currentUserId) return t('donations:manual.byYou')
  const name = donation.agentUserName?.trim()
  return name ? t('donations:manual.byName', { name }) : null
}

const isManualMethod = (value: string): value is ManualPaymentMethod =>
  (MANUAL_PAYMENT_METHODS as readonly string[]).includes(value)

/**
 * Libellé du moyen de paiement d'une ligne : la liste FERMÉE d'un don manuel
 * est traduite (`ESPECES` ⇒ « Espèces ») ; le texte libre d'AlloDons
 * (« Carte bancaire ») est rendu tel quel.
 */
export function paymentMethodLabel(
  t: TFunction,
  donation: Pick<DonationRecord, 'source' | 'paymentMethod'>,
): string | null {
  const method = donation.paymentMethod
  if (!method) return null
  if (donation.source === 'MANUAL' && isManualMethod(method)) {
    return t(`donations:manual.paymentMethods.${method}`)
  }
  return method
}
